import { AppCard, AppPage } from '../../components/general';
import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { DataTableSearchCreateHeader } from '../../components/datatable';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useNavigate } from 'react-router-dom';
import { FeeConfigurationViewModel, FeeHudLineViewModel, FeeRulesVersionViewModel, ExcelImportResult } from '../../models/fees';
import { confirmPopup, ConfirmPopup } from 'primereact/confirmpopup';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { PublishVersionDialog } from './PublishVersionDialog';
import { ImportFeeConfigurationsDialog } from './ImportFeeConfigurationsDialog';

const TABLE_ROW_COUNT = 10;

interface FeesProps {
  feeConfigurations: FeeConfigurationViewModel[];
  hudLines: FeeHudLineViewModel[];
  versions: FeeRulesVersionViewModel[];
  selectedVersionId: number | null;
  onVersionChange: (versionId: number | null) => void;
  onPublish: (description: string) => Promise<void>;
  onImport: (file: File) => Promise<ExcelImportResult>;
  onDelete: (id: number) => void;
}

export const Fees: FC<FeesProps> = ({ feeConfigurations, hudLines, versions, selectedVersionId, onVersionChange, onPublish, onImport, onDelete }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedHudLineId, setSelectedHudLineId] = useState<number | null>(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsCompactView(window.innerWidth <= 1140);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setSelectedHudLineId(null);
  }, [selectedVersionId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        filterMenuRef.current &&
        filterButtonRef.current &&
        !filterMenuRef.current.contains(target) &&
        !filterButtonRef.current.contains(target) &&
        !(target as Element).closest('.p-dropdown-panel')
      ) {
        setShowFilterMenu(false);
      }
    };

    if (showFilterMenu) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFilterMenu]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedHudLineId !== null && selectedHudLineId !== undefined) count++;
    return count;
  }, [selectedHudLineId]);

  const versionOptions = useMemo(() => {
    const options = versions
      .sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0))
      .map((version) => ({
        label: `${version.versionNumber}${version.description ? ` - ${version.description}` : ''}`,
        value: version.feeRulesVersionId,
      }));
    return [{ label: 'Draft', value: null }, ...options];
  }, [versions]);

  const hudLineOptions = useMemo(() => {
    const options = hudLines.map((hudLine) => ({
      label: hudLine.hudLine ? `${hudLine.hudLine}${hudLine.description ? ` - ${hudLine.description}` : ''}` : `HUD Line ${hudLine.feeHudLineId}`,
      value: hudLine.feeHudLineId,
    }));
    return [{ label: 'All HUD Lines', value: null }, ...options];
  }, [hudLines]);

  const hudLineMap = useMemo(() => {
    return new Map(hudLines.map((hudLine) => [hudLine.feeHudLineId, hudLine]));
  }, [hudLines]);

  const draftConfigCount = useMemo(() => {
    return feeConfigurations.filter(config => !config.feeRulesVersionId).length;
  }, [feeConfigurations]);

  const draftHudLineCount = useMemo(() => {
    return hudLines.filter(hudLine => !hudLine.feeRulesVersionId).length;
  }, [hudLines]);

  const filtered = useMemo(() => {
    if (!feeConfigurations || feeConfigurations.length === 0) {
      return [];
    }
    
    const result = feeConfigurations.filter((config) => {
      const matchesSearch =
        !search ||
        (config.description?.toLowerCase().includes(search.toLowerCase()) ?? false);
      
      const matchesHudLine = 
        selectedHudLineId === null || 
        selectedHudLineId === undefined || 
        config.feeHudLineId === selectedHudLineId;

      const matchesVersion = 
        selectedVersionId === null 
          ? !config.feeRulesVersionId
          : config.feeRulesVersionId === selectedVersionId;

      return matchesSearch && matchesHudLine && matchesVersion;
    }).map((config) => {
      const hudLine = hudLineMap.get(config.feeHudLineId);
      return {
        ...config,
        rulesCount: config.rules?.length || 0,
        hudLineValue: hudLine?.hudLine || '',
      };
    });
    
    return result;
  }, [feeConfigurations, search, selectedHudLineId, selectedVersionId, hudLineMap]);

  return (
    <AppPage 
      title="Fees" 
      breadcrumbs="Admin - Fees"
      headerEnd={
        <div className="flex items-center gap-2" style={{ alignItems: 'stretch' }}>
          <label className="text-sm text-gray-600 whitespace-nowrap flex items-center">Version:</label>
          <Dropdown
            value={selectedVersionId}
            options={versionOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => onVersionChange(e.value)}
            placeholder="Select Version"
            className="w-80 border-1 border-solid border-gray-300"
          />
          {selectedVersionId === null && (
            <Button
              icon="pi pi-check"
              label="Publish"
              className="p-button-sm p-button-success"
              onClick={() => setShowPublishDialog(true)}
              disabled={draftConfigCount === 0 && draftHudLineCount === 0}
              style={(draftConfigCount === 0 && draftHudLineCount === 0) ? { opacity: 0.3 } : {}}
            />
          )}
        </div>
      }
    >
      <AppCard>
        <div className="flex justify-between mb-4">
          <div className="flex gap-2 items-center relative">
            <DataTableSearchCreateHeader createNewDisabled onSearchChange={(value: string) => setSearch(value)} />
            <div ref={filterButtonRef}>
              <Button
                icon="pi pi-filter"
                label="Filter"
                className="p-button-secondary"
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                aria-controls="filter-menu"
                aria-haspopup
              >
                {activeFilterCount > 0 && (
                  <span className="p-badge p-component ml-2" style={{ minWidth: '1.2rem', height: '1.2rem', lineHeight: '1.2rem', fontSize: '0.75rem' }}>
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </div>
            {showFilterMenu && (
              <div
                ref={filterMenuRef}
                className="absolute top-full mt-2 bg-white border-1 border-gray-300 rounded shadow-lg z-50"
                style={{ minWidth: '320px', right: 0 }}
              >
                <div className="flex flex-column gap-3 p-3">
                  <div className="flex flex-column gap-2">
                    <label className="text-sm font-semibold">HUD Line</label>
                    <Dropdown
                      value={selectedHudLineId}
                      options={hudLineOptions}
                      optionLabel="label"
                      optionValue="value"
                      onChange={(e) => setSelectedHudLineId(e.value ?? null)}
                      placeholder="Select HUD Line"
                      className="w-full border-1 border-solid border-gray-300"
                      showClear
                      filter
                      filterPlaceholder="Search HUD Lines"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Tooltip target=".create-config-btn" />
            <Button
              icon="pi pi-plus"
              label={!isCompactView ? 'Create Configuration' : undefined}
              className="p-button-sm p-button-primary create-config-btn"
              onClick={() => navigate('/fees/create')}
              disabled={selectedVersionId !== null}
              style={{
                ...(selectedVersionId !== null ? { opacity: 0.3 } : {}),
                ...(isCompactView ? { width: '2.5rem', height: '2.5rem', padding: 0 } : {}),
              }}
              tooltip={isCompactView ? 'Create Configuration' : undefined}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Tooltip target=".import-btn" />
            <Button
              icon="pi pi-upload"
              label={!isCompactView ? 'Import' : undefined}
              className="p-button-sm p-button-secondary import-btn"
              onClick={() => setShowImportDialog(true)}
              disabled={selectedVersionId !== null}
              style={{
                ...(selectedVersionId !== null ? { opacity: 0.3 } : {}),
                ...(isCompactView ? { width: '2.5rem', height: '2.5rem', padding: 0 } : {}),
              }}
              tooltip={isCompactView ? 'Import' : undefined}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Tooltip target=".manage-hud-btn" />
            <Button
              icon="pi pi-list"
              label={!isCompactView ? 'Manage HUD Lines' : undefined}
              className="p-button-sm p-button-secondary manage-hud-btn"
              onClick={() => navigate(selectedVersionId ? `/fees/hud-lines?versionId=${selectedVersionId}` : '/fees/hud-lines')}
              style={isCompactView ? { width: '2.5rem', height: '2.5rem', padding: 0 } : undefined}
              tooltip={isCompactView ? 'Manage HUD Lines' : undefined}
              tooltipOptions={{ position: 'bottom' }}
            />
          </div>
        </div>

        <DataTable
          value={filtered}
          paginator
          rows={TABLE_ROW_COUNT}
          rowsPerPageOptions={[10, 25, 50, 100]}
          stripedRows
          emptyMessage="No fee configurations found"
          className="p-datatable-sm p-datatable-gridlines"
          resizableColumns
          columnResizeMode="fit"
          sortField="hudLineValue"
          sortOrder={1}
        >
          <Column
            field="hudLineValue"
            header="HUD Line / Description"
            body={(row) => {
              const hudLine = hudLineMap.get(row.feeHudLineId);
              const hudLineValue = hudLine?.hudLine;
              const description = row.description ?? '-';
              return (
                <div
                  style={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    lineHeight: '1.5',
                  }}
                >
                  {`${hudLineValue ? `${hudLineValue} - ` : ''}${description}`}
                </div>
              );
            }}
            sortable
            style={{ minWidth: '300px' }}
          />
          <Column
            field="rulesCount"
            header="Rules Count"
            sortable
            style={{ minWidth: '10px' }}
          />
          <Column
            header="Actions"
            style={{ width: '8rem', minWidth: '8rem' }}
            body={(row) => {
              const isDraft = !row.feeRulesVersionId;
              return (
                <div className="flex gap-1">
                  <Button
                    icon={isDraft ? 'pi pi-pencil' : 'pi pi-eye'}
                    className="p-button-text p-button-sm"
                    onClick={() => {
                      const versionId = row.feeRulesVersionId;
                      navigate(versionId ? `/fees/edit/${row.feeConfigurationId}?versionId=${versionId}` : `/fees/edit/${row.feeConfigurationId}`);
                    }}
                    tooltip={isDraft ? 'Edit' : 'View'}
                    tooltipOptions={{
                      position: 'top',
                    }}
                  />
                  {isDraft ? (
                    <Button
                      icon="pi pi-trash"
                      className="p-button-text p-button-sm p-button-danger"
                      onClick={(e) => {
                        confirmPopup({
                          target: e.currentTarget,
                          message: 'Are you sure you want to delete this fee configuration?',
                          icon: 'pi pi-exclamation-triangle',
                          acceptClassName: 'p-button-danger p-button-sm',
                          accept: () => onDelete(row.feeConfigurationId),
                          reject: () => {},
                        });
                      }}
                    />
                  ) : (
                    <Button
                      icon="pi pi-trash"
                      className="p-button-text p-button-sm p-button-danger"
                      disabled={true}
                      style={{ opacity: 0.3 }}
                    />
                  )}
                </div>
              );
            }}
          />
        </DataTable>
        <div className="mt-3 text-sm text-gray-600">
          Total: {filtered.length} {filtered.length === 1 ? 'configuration' : 'configurations'}
        </div>
      </AppCard>
      <ConfirmPopup />
      <PublishVersionDialog
        visible={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
        onPublish={onPublish}
        draftConfigCount={draftConfigCount}
        draftHudLineCount={draftHudLineCount}
      />
      <ImportFeeConfigurationsDialog
        visible={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImport={onImport}
      />
    </AppPage>
  );
};

